import {Component, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatStepper, MatStepperModule} from '@angular/material/stepper';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {InfoMessageService} from '../../services/infomessage/infomessage.service';
import {AuthenticationService} from '../../services/authentication/authentication.service';
import {Subject, takeUntil} from 'rxjs';
import {IdDocumentModel, RegistrationModel} from '../../models/registration/registration.model';
import {STEPPER_GLOBAL_OPTIONS} from '@angular/cdk/stepper';
import {GenderService} from '../../services/gender/gender.service';
import {iResponseList} from '../../models/response/response.model';
import {GenderModel} from '../../models/gender/gender.model';
import {MatSelectModule} from '@angular/material/select';
import {GenderPipe} from '../../pipe/gender/gender.pipe';
import {Router, RouterLink} from '@angular/router';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';
import {FileUploadComponent} from '../file-upload/file-upload.component';
import {FileModel} from '../../models/file/file.model';
import {DeviceDetectionService} from '../../services/device-detection/device-detection.service';
import {passwordValidator} from '../../validators/password-validator';
import {NgClass} from '@angular/common';
import {MatDialog} from '@angular/material/dialog';
import {
    ResendRegisterLinkModalComponent
} from '../modals/resend-register-link-modal/resend-register-link-modal.component';


@Component({
    selector: 'app-registration',
    imports: [
        MatStepperModule,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatSelectModule,
        GenderPipe,
        RouterLink,
        MatDatepicker,
        MatDatepickerInput,
        MatDatepickerToggle,
        FileUploadComponent,
        NgClass,
    ],
    providers: [
        {
            provide: STEPPER_GLOBAL_OPTIONS,
            useValue: {showError: true},
        },
    ],
    templateUrl: './registration.component.html',
    styleUrl: './registration.component.sass'
})
export class RegistrationComponent implements OnInit, OnDestroy {
    private _formBuilder = inject(FormBuilder);
    private destroy$ = new Subject<void>();
    @ViewChild('stepper') private stepper!: MatStepper;

    generalFormGroup: FormGroup = this._formBuilder.group(
        {
            genderId: [''],
            firstName: ['', Validators.required],
            lastName: ['', Validators.required],
            dateOfBirth: ['', Validators.required]
        }
    );
    contactFormGroup: FormGroup = this._formBuilder.group(
        {
            email: ['', [Validators.required, Validators.email]],
            mobileNumber: [''],
            phoneNumber: ['', Validators.required],
        }
    );
    addressFormGroup: FormGroup = this._formBuilder.group(
        {
            city: ['', Validators.required],
            street: ['', Validators.required],
            houseNumber: ['', Validators.required],
            zipCode: ['', Validators.required]
        }
    );
    finishFormGroup: FormGroup = this._formBuilder.group(
        {
            password: ['', [Validators.required, passwordValidator()]],
            passwordConfirm: ['']
        }
    );

    genders: GenderModel[] = [];

    idDocuments: IdDocumentModel = {
        front: {
            fileData: '',
            fileName: '',
            mimeType: ''
        },
        back: {
            fileData: '',
            fileName: '',
            mimeType: ''
        }
    };

    isMobile: boolean = false;
    hidePassword: boolean = true;
    hidePasswordConfirm: boolean = true;
    registerSuccess: boolean = false;

    constructor(
        private infoMessageService: InfoMessageService,
        private authenticationService: AuthenticationService,
        private genderService: GenderService,
        private deviceDetectionService: DeviceDetectionService,
        private dialog: MatDialog,
        private router: Router
    ) {
        this.isMobile = deviceDetectionService.getIsMobile();
    }

    ngOnInit(): void {
        this.getGenders();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    checkFormValidity(form: FormGroup): void {
        if (form.invalid) {
            form.markAllAsTouched();
            return;
        }
    }

    onFileSelectedFront(file: FileModel) {
        this.idDocuments.front = file;
    }

    onFileSelectedBack(file: FileModel) {
        this.idDocuments.back = file;
    }

    removeFile(type: string) {
        if (type === 'front') {
            this.idDocuments.front.fileData = '';
            this.idDocuments.front.fileName = '';
            this.idDocuments.front.mimeType = '';
        } else {
            this.idDocuments.back.fileData = '';
            this.idDocuments.back.fileName = '';
            this.idDocuments.back.mimeType = '';
        }
    }

    validateStep(): void {
        if (this.areIdDocumentsValid()) {
            this.stepper.next();
        } else {
            this.infoMessageService.createMessage('Bitte Ausweisdokumente hochladen', 'error')
            return;
        }
    }

    areIdDocumentsValid(): boolean {
        return this.idDocuments.front.fileData != '' && this.idDocuments.back.fileData != '';
    }

    onSubmit() {
        if (this.generalFormGroup.invalid || this.contactFormGroup.invalid || this.addressFormGroup.invalid || this.finishFormGroup.invalid) {
            this.generalFormGroup.markAllAsTouched()
            this.contactFormGroup.markAllAsTouched()
            this.addressFormGroup.markAllAsTouched()
            this.finishFormGroup.markAllAsTouched()
            return;
        }

        if (this.finishFormGroup.value.password !== this.finishFormGroup.value.passwordConfirm) {
            return;
        }

        if (this.idDocuments.front.fileData == '' || this.idDocuments.back.fileData == '') {
            this.infoMessageService.createMessage('Bitte Ausweisdokumente hochladen', 'error')
            return;
        }

        let genderId: string | null = null;
        if (this.generalFormGroup.value.genderId === '') {
            genderId = null;
        } else {
            genderId = this.generalFormGroup.value.genderId
        }

        const registrationModel: RegistrationModel = {
            deliveryAddress: {...this.addressFormGroup.value},
            invoiceAddress: {...this.addressFormGroup.value},
            email: this.contactFormGroup.value.email,
            firstName: this.generalFormGroup.value.firstName,
            genderId: genderId,
            lastName: this.generalFormGroup.value.lastName,
            mobileNumber: this.contactFormGroup.value.mobileNumber,
            password: this.finishFormGroup.value.password,
            phoneNumber: this.contactFormGroup.value.phoneNumber,
            skype: '',
            title: '',
            dateOfBirth: this.generalFormGroup.value.dateOfBirth,
            idDocuments: this.idDocuments
        }
        this.registration(registrationModel)
    }

    openResendLinkModal(): void {
        const dialogRef = this.dialog.open(ResendRegisterLinkModalComponent, {
            disableClose: true,
            closeOnNavigation: false
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.infoMessageService.createMessage('Ihre Anfrage wurde verschickt')
            }
        })
    }

    private getGenders(): void {
        this.genderService.getGenders()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponseList<GenderModel>) => {
                    this.genders = response.data;
                },
                error: (error) => {
                }
            })
    }

    private registration(registrationModel: RegistrationModel): void {
        this.authenticationService.registration(registrationModel)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.registerSuccess = true;
                    // this.openRegistrationSuccessModal();
                },
                error: (error) => {
                    this.registerSuccess = false;
                }
            })
    }

}
